// 3rd party Libs
import { isEqual } from 'lodash-es';
import { select } from 'd3-selection';
import { useLocation } from '@reach/router';
import { FormattedMessage, useIntl } from 'react-intl';
import React, { useEffect, useState, useContext, useRef, useCallback, Fragment } from 'react';

import { OverflowMenu, OverflowMenuItem } from '@carbon/react';
import { CaretDown } from '@carbon/icons-react';
import { navigate, Link } from 'gatsby';

// Locals
import cn from 'clsx';
import * as css from './Layout.module.scss';
import { allProducts as PRODUCTS } from 'data/links';
import { Header, HeaderDropdown, Academy } from 'components';
import LangContext from 'lib/langContext';

// Defaults from carbon
import { LeftNav } from 'src/components/LeftNav';
import { Footer } from 'src/components/Footer';
import { Container } from 'src/components/Container';
import 'src/styles/index.scss';

// Polifills and data
import '@formatjs/intl-pluralrules/locale-data/en';
import '@formatjs/intl-pluralrules/locale-data/es';
// import '@formatjs/intl-pluralrules/locale-data/fa';
import '@formatjs/intl-pluralrules/locale-data/ru';
import '@formatjs/intl-relativetimeformat/locale-data/en';
import '@formatjs/intl-relativetimeformat/locale-data/es';
// import '@formatjs/intl-relativetimeformat/locale-data/fa';
import '@formatjs/intl-relativetimeformat/locale-data/ru';

function RenderDebugToc(): ReactElement | null {
    type TocItem = { id: null | string; text: string; level: number };
    const [toc, setToc] = useState<TocItem[]>([]);

    // Get all headings on the page
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const headings = Array.from(document.querySelectorAll('h1, h2, h3, h4, h5, h6'));
        const r = headings.map<TocItem>(x => {
            const id = x.getAttribute('id');
            const text = String(x.textContent);
            const level = parseInt(x.tagName.replace('H', ''), 10);
            return { id, text, level };
        });

        if (!isEqual(r, toc)) setToc(r);
    });

    if (toc.length === 0) return null;

    return (
        <div data-theme="dark" className={css.debugTOC}>
            <div className={css.stats}>
                <span>
                    Count: <strong children={toc.length} />
                </span>
                <span>
                    Depth: <strong children={toc.reduce((acc, x) => Math.max(acc, x.level), 0)} />
                </span>
            </div>
            <ul
                children={toc.map(({ level, text, id }, index, array) => {
                    const prev = array[index - 1] as undefined | TocItem;
                    const href = id ? `#${id}` : null;
                    const txt = `H${level}. ${text}`;

                    return (
                        <li
                            key={index}
                            style={{ marginLeft: (level - 1) * 16 }}
                            className={cn((prev?.level ?? 0) > level && css.newSection)}
                            children={href ? <Link to={href} children={txt} /> : <span children={txt} />}
                        />
                    );
                })}
            />
        </div>
    );
}

function LanguagesDropdown() {
    const int = useIntl();
    const lng = useContext(LangContext);
    const { pathname } = useLocation();

    return (
        <HeaderDropdown
            direction="left"
            arrow
            toggle={lng.current.key.toUpperCase()}
            title={int.formatMessage({ defaultMessage: 'Select language' })}
            items={lng.langs.map(x => {
                const generatedLink = pathname.replace(`/${lng.current.key}/`, `/${x.langKey}/`);
                const link = lng.localizedLinks.includes(generatedLink) ? generatedLink : `/${x.langKey}/`;

                return {
                    icon: x.langKey.toUpperCase(),
                    label: x.label,
                    disabled: x.selected,
                    onClick: () => navigate(link, {}),
                };
            })}
        />
    );
}

export type LayoutProps = {
    // Special casing
    homepage: boolean;
    theme: 'dark' | 'white' | 'g10';
    // Content
    tabs?: Array<unknown>;
    children: ReactNode | ReactNode[];
};
export default function Layout(props: LayoutProps) {
    const { children, homepage, theme, tabs } = props;

    useEffect(() => {
        // This has to be imported and used inside a `useEffect`
        // because it gets `window` usages into the code
        // and that breaks the gatsby SSR build

        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const smoothScroll = require('smooth-scroll');

        // Set theme
        document.body.setAttribute('data-theme', theme);

        const scroll = new smoothScroll('a[href*="#"]', {
            speed: 400,
            durationMin: 250,
            durationMax: 700,
            easing: 'easeInOutCubic',
            clip: true,
            offset: tabs ? 112 : 64,
        });
        return scroll.destroy;
    }, [tabs, theme]);

    // @ts-expect-error: What the fuck do I know how they decided that this is a good idea‽
    const is404 = children?.key === null;
    const scrollTicking = useRef<boolean>(false);
    const lastKnownScrollPosition = useRef<number>(0);
    const handleScrollThrottled = useCallback((): void => {
        if (lastKnownScrollPosition.current >= 36) {
            select(document.documentElement).classed(css.isFixed, true);
        } else {
            select(document.documentElement).classed(css.isFixed, false);
        }
    }, []);
    const handleScroll = useCallback((): void => {
        // https://developer.mozilla.org/en-US/docs/Web/API/Document/scroll_event
        lastKnownScrollPosition.current = document.scrollingElement?.scrollTop ?? 0;
        if (!scrollTicking.current) {
            window.requestAnimationFrame(() => {
                handleScrollThrottled();
                scrollTicking.current = false;
            });
            scrollTicking.current = true;
        }
    }, [handleScrollThrottled]);

    useEffect(() => {
        document.addEventListener('scroll', handleScroll);
        return () => document.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    return (
        <Fragment>
            <nav className={cn(css.linksBarWrapper, css.top)}>
                <div
                    className={css.linksBarLinks}
                    children={PRODUCTS.map(({ label, url, active, sup }, i) => {
                        /* eslint-disable gatsby/use-gatsby-link */
                        return (
                            <a key={i} href={url} className={cn(active === true && css.activeLink)}>
                                {label}
                                {sup && <span className={css.badge} children={sup} />}
                            </a>
                        );
                        /* eslint-enable gatsby/use-gatsby-link */
                    })}
                />
                <OverflowMenu
                    aria-label="overflow-menu"
                    direction="bottom"
                    flipped
                    focusTrap={false}
                    className={css.linksBarOverflowButton}
                    menuOptionsClass={css.linksBarOverflowMenu}
                    renderIcon={() => {
                        return (
                            <span className={css.linksBarOverflowMenuLabel}>
                                <FormattedMessage defaultMessage="More" />
                                &nbsp;
                                <CaretDown size={16} />
                            </span>
                        );
                    }}
                    children={PRODUCTS.map(({ label, url, active, sup }, i) => {
                        return (
                            <OverflowMenuItem
                                key={i}
                                href={url}
                                disabled={active}
                                itemText={
                                    <>
                                        {label}
                                        {sup && <span className={css.badge} children={<sup children={sup} />} />}
                                    </>
                                }
                                className={cn(active === true && css.activeLink)}
                            />
                        );
                    })}
                />
            </nav>

            <Header className={css.cdsHeader} contentLeft={<Academy />} contentRight={<LanguagesDropdown />} />

            <LeftNav homepage={homepage} is404Page={is404} theme={theme} />

            <Container homepage={homepage} theme={theme}>
                {children}

                <Footer />

                {process.env.NODE_ENV === 'development' && <RenderDebugToc />}
            </Container>
        </Fragment>
    );
}
